import { VoiceCoreAnimation, type VoiceCoreState } from "./voice-core-animation.js";

export type LocalSpeechGateConfig = {
  minThreshold: number;
  maxThreshold: number;
  noiseMultiplier: number;
  attackFrames: number;
  releaseFrames: number;
  duckMs: number;
};

export const DEFAULT_LOCAL_SPEECH_GATE: LocalSpeechGateConfig = {
  minThreshold: 0.035,
  maxThreshold: 0.16,
  noiseMultiplier: 2.4,
  attackFrames: 3,
  releaseFrames: 10,
  duckMs: 450,
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function rmsLevel(data: Uint8Array) {
  let sum = 0;
  for (const sample of data) {
    const normalized = (sample - 128) / 128;
    sum += normalized * normalized;
  }
  return Math.min(1, Math.sqrt(sum / Math.max(1, data.length)));
}

export function visualAudioLevel(rawLevel: number, noiseFloor = 0.008) {
  const floor = clamp(Math.max(0.003, noiseFloor * 0.85), 0.003, 0.04);
  const normalized = clamp((rawLevel - floor) / Math.max(0.04, 0.115 - floor), 0, 1);
  return normalized < 0.015 ? 0 : Math.pow(normalized, 0.72);
}

export function waveIntensity(radius: number, progress: number, strength: number) {
  const distance = Math.abs(radius - progress);
  return Math.max(0, Math.min(1, (1 - distance / 0.2) * strength));
}

export function shouldDuckAssistant(input: { enabled: boolean; assistantSpeaking: boolean; authoritativeUserSpeaking: boolean; locallyPredictedSpeech: boolean; localDuckUntil: number; now: number }) {
  return input.enabled
    && input.assistantSpeaking
    && (input.authoritativeUserSpeaking || (input.locallyPredictedSpeech && input.now < input.localDuckUntil));
}

type SpeechGateOptions = LocalSpeechGateConfig & { initialNoiseFloor?: number; releaseRatio?: number };

export class SpeechGate {
  private attack = 0;
  private release = 0;
  private active = false;
  private readonly adaptive: boolean;
  private readonly minThreshold: number;
  private readonly maxThreshold: number;
  private readonly noiseMultiplier: number;
  private readonly attackFrames: number;
  private readonly releaseFrames: number;
  private readonly releaseRatio: number;
  private fixedThreshold: number;
  private fixedReleaseThreshold: number;
  private noiseFloor: number;
  private threshold: number;

  constructor(options?: SpeechGateOptions);
  constructor(threshold?: number, releaseThreshold?: number, attackFrames?: number, releaseFrames?: number);
  constructor(optionsOrThreshold: SpeechGateOptions | number = DEFAULT_LOCAL_SPEECH_GATE, releaseThreshold = 0.055, attackFrames = 5, releaseFrames = 12) {
    if (typeof optionsOrThreshold === "number") {
      this.adaptive = false;
      this.fixedThreshold = optionsOrThreshold;
      this.fixedReleaseThreshold = releaseThreshold;
      this.minThreshold = optionsOrThreshold;
      this.maxThreshold = optionsOrThreshold;
      this.noiseMultiplier = 1;
      this.attackFrames = attackFrames;
      this.releaseFrames = releaseFrames;
      this.releaseRatio = releaseThreshold / Math.max(optionsOrThreshold, 0.0001);
      this.noiseFloor = 0;
      this.threshold = optionsOrThreshold;
      return;
    }

    this.adaptive = true;
    this.minThreshold = Math.max(0.001, optionsOrThreshold.minThreshold);
    this.maxThreshold = Math.max(this.minThreshold, optionsOrThreshold.maxThreshold);
    this.noiseMultiplier = Math.max(1, optionsOrThreshold.noiseMultiplier);
    this.attackFrames = Math.max(1, Math.round(optionsOrThreshold.attackFrames));
    this.releaseFrames = Math.max(1, Math.round(optionsOrThreshold.releaseFrames));
    this.releaseRatio = clamp(optionsOrThreshold.releaseRatio ?? 0.58, 0.2, 0.9);
    this.noiseFloor = Math.max(0, optionsOrThreshold.initialNoiseFloor ?? 0.012);
    this.fixedThreshold = this.minThreshold;
    this.fixedReleaseThreshold = this.minThreshold * this.releaseRatio;
    this.threshold = this.derivedThreshold();
  }

  private derivedThreshold() {
    return clamp(Math.max(this.minThreshold, this.noiseFloor * this.noiseMultiplier), this.minThreshold, this.maxThreshold);
  }

  update(level: number) {
    const sample = clamp(level, 0, 1);
    if (this.adaptive) {
      const threshold = this.derivedThreshold();
      if (!this.active && sample < threshold * 0.82) {
        this.noiseFloor += (sample - this.noiseFloor) * 0.06;
      }
      this.threshold = this.derivedThreshold();
      this.fixedReleaseThreshold = this.threshold * this.releaseRatio;
    }

    if (sample >= this.threshold) {
      this.attack += 1;
      this.release = 0;
      if (this.attack >= this.attackFrames) this.active = true;
    } else if (sample <= this.fixedReleaseThreshold) {
      this.release += 1;
      this.attack = 0;
      if (this.release >= this.releaseFrames) this.active = false;
    } else {
      this.attack = 0;
      this.release = 0;
    }
    return this.active;
  }

  diagnostics() {
    return { active: this.active, noiseFloor: this.noiseFloor, threshold: this.threshold, releaseThreshold: this.fixedReleaseThreshold };
  }

  reset() {
    this.attack = 0;
    this.release = 0;
    this.active = false;
    if (this.adaptive) {
      this.noiseFloor = 0.012;
      this.threshold = this.derivedThreshold();
      this.fixedReleaseThreshold = this.threshold * this.releaseRatio;
    }
  }
}

export class ReactiveAudioController {
  private context: AudioContext | null = null;
  private localSource: MediaStreamAudioSourceNode | null = null;
  private remoteSource: MediaStreamAudioSourceNode | null = null;
  private localAnalyser: AnalyserNode | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  private analysisSink: GainNode | null = null;
  private frame = 0;
  private smoothedLocal = 0;
  private smoothedRemote = 0;
  private authoritativeUserSpeaking = false;
  private assistantSpeaking = false;
  private locallyPredictedSpeech = false;
  private previousLocalPrediction = false;
  private localDuckUntil = 0;
  private targetPlaybackVolume = 1;
  private playbackVolume = 1;
  private previousVisualLevel = 0;
  private lastVisualPulseAt = 0;
  private lastDiagnosticAt = 0;
  private readonly speechGate: SpeechGate;
  private readonly visual: VoiceCoreAnimation;
  private readonly localDuckMs: number;

  constructor(
    private readonly core: HTMLElement,
    private readonly duckingEnabled: boolean,
    private readonly remoteAudio: HTMLAudioElement,
    speechGateConfig: LocalSpeechGateConfig = DEFAULT_LOCAL_SPEECH_GATE,
  ) {
    this.speechGate = new SpeechGate(speechGateConfig);
    this.localDuckMs = Math.min(450, Math.max(100, speechGateConfig.duckMs));
    this.visual = new VoiceCoreAnimation(core);
    this.visual.setState((core.dataset.mode as VoiceCoreState | undefined) ?? "idle");
  }

  async attachLocal(stream: MediaStream) {
    const context = this.ensureContext();
    await context.resume();
    this.localSource?.disconnect();
    this.localAnalyser?.disconnect();
    this.localAnalyser = this.createAnalyser(context);
    this.localSource = context.createMediaStreamSource(stream);
    this.localSource.connect(this.localAnalyser);
    this.start();
  }

  async attachRemote(stream: MediaStream) {
    const context = this.ensureContext();
    await context.resume();
    this.remoteSource?.disconnect();
    this.remoteAnalyser?.disconnect();
    this.analysisSink?.disconnect();
    this.remoteAudio.srcObject = stream;
    this.remoteAudio.muted = false;
    this.remoteAudio.volume = 1;
    void this.remoteAudio.play().catch(() => { /* autoplay retries when media becomes playable */ });
    this.remoteAnalyser = this.createAnalyser(context);
    this.analysisSink = context.createGain();
    this.analysisSink.gain.setValueAtTime(0, context.currentTime);
    this.remoteSource = context.createMediaStreamSource(stream);
    this.remoteSource.connect(this.remoteAnalyser);
    this.remoteAnalyser.connect(this.analysisSink);
    this.analysisSink.connect(context.destination);
    this.start();
  }

  setState(state: VoiceCoreState) {
    this.visual.setState(state);
  }

  setAuthoritativeUserSpeaking(active: boolean) {
    if (active && !this.authoritativeUserSpeaking) this.visual.pulse(0.58);
    this.authoritativeUserSpeaking = active;
    this.applyDucking();
  }

  setAssistantSpeaking(active: boolean) {
    if (active && !this.assistantSpeaking) this.visual.pulse(0.66);
    this.assistantSpeaking = active;
    this.applyDucking();
  }

  private ensureContext() {
    this.context ??= new AudioContext();
    return this.context;
  }

  private createAnalyser(context: AudioContext) {
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
    return analyser;
  }

  private start() {
    if (this.frame) return;
    this.core.dataset.audio = "live";
    const render = () => {
      const local = this.read(this.localAnalyser);
      const remote = this.read(this.remoteAnalyser);
      this.smoothedLocal += (local - this.smoothedLocal) * 0.28;
      this.smoothedRemote += (remote - this.smoothedRemote) * 0.24;
      this.locallyPredictedSpeech = this.speechGate.update(this.smoothedLocal);
      if (this.locallyPredictedSpeech && !this.previousLocalPrediction) this.localDuckUntil = Date.now() + this.localDuckMs;
      this.previousLocalPrediction = this.locallyPredictedSpeech;
      this.applyDucking();
      this.playbackVolume += (this.targetPlaybackVolume - this.playbackVolume) * (this.targetPlaybackVolume < this.playbackVolume ? 0.32 : 0.18);
      this.remoteAudio.volume = Math.max(0, Math.min(1, this.playbackVolume));
      const rawLevel = this.assistantSpeaking ? this.smoothedRemote : this.smoothedLocal;
      const noiseFloor = this.assistantSpeaking ? 0.004 : this.speechGate.diagnostics().noiseFloor;
      let visualLevel = visualAudioLevel(rawLevel, noiseFloor);
      if (this.assistantSpeaking || this.authoritativeUserSpeaking) visualLevel = Math.max(0.14, visualLevel);
      this.emitDiagnostics(local, remote, visualLevel);
      this.visual.setEnergy(visualLevel);
      const now = performance.now();
      this.previousVisualLevel += (visualLevel - this.previousVisualLevel) * 0.045;
      const meaningfulPeak = visualLevel > Math.max(0.2, this.previousVisualLevel + 0.085);
      if (meaningfulPeak && now - this.lastVisualPulseAt > 950) {
        this.visual.pulse(Math.min(1, 0.4 + visualLevel * 1.6));
        this.lastVisualPulseAt = now;
      }
      this.frame = requestAnimationFrame(render);
    };
    this.frame = requestAnimationFrame(render);
  }

  private read(analyser: AnalyserNode | null) {
    if (!analyser) return 0;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    return rmsLevel(data);
  }

  private applyDucking() {
    this.setDucked(shouldDuckAssistant({
      enabled: this.duckingEnabled,
      assistantSpeaking: this.assistantSpeaking,
      authoritativeUserSpeaking: this.authoritativeUserSpeaking,
      locallyPredictedSpeech: this.locallyPredictedSpeech,
      localDuckUntil: this.localDuckUntil,
      now: Date.now(),
    }));
  }

  private setDucked(ducked: boolean) {
    this.targetPlaybackVolume = ducked ? 0.28 : 1;
    this.core.toggleAttribute("data-ducked", ducked);
  }

  private emitDiagnostics(local: number, remote: number, visualLevel: number) {
    if (typeof location === "undefined" || !["localhost", "127.0.0.1", "::1"].includes(location.hostname)) return;
    const now = Date.now();
    if (now - this.lastDiagnosticAt < 500) return;
    this.lastDiagnosticAt = now;
    console.debug("[voice-diag]", {
      localRms: Number(local.toFixed(4)),
      remoteRms: Number(remote.toFixed(4)),
      visualLevel: Number(visualLevel.toFixed(3)),
      gate: this.speechGate.diagnostics(),
      authoritativeUserSpeaking: this.authoritativeUserSpeaking,
      locallyPredictedSpeech: this.locallyPredictedSpeech,
      assistantSpeaking: this.assistantSpeaking,
      ducked: this.targetPlaybackVolume < 1,
    });
  }

  async stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.localSource?.disconnect();
    this.remoteSource?.disconnect();
    this.localAnalyser?.disconnect();
    this.remoteAnalyser?.disconnect();
    this.analysisSink?.disconnect();
    this.localSource = null;
    this.remoteSource = null;
    this.localAnalyser = null;
    this.remoteAnalyser = null;
    this.analysisSink = null;
    this.remoteAudio.pause();
    this.remoteAudio.srcObject = null;
    this.remoteAudio.volume = 1;
    this.targetPlaybackVolume = 1;
    this.playbackVolume = 1;
    this.previousLocalPrediction = false;
    this.localDuckUntil = 0;
    this.previousVisualLevel = 0;
    this.lastVisualPulseAt = 0;
    this.speechGate.reset();
    this.visual.destroy();
    this.core.removeAttribute("data-audio");
    this.core.removeAttribute("data-ducked");
    if (this.context && this.context.state !== "closed") await this.context.close();
    this.context = null;
  }
}
